import React, { useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import { id } from "date-fns/locale";
import {
  IconEye,
  IconEyeOff,
  IconFileSpreadsheet,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useAttendance } from "contexts/v1/masterData/AttendanceContext";
import { useUser } from "contexts/v1/masterData/UserContext";
import { useSchedule } from "../context/ScheduleContext";
import { useShifts } from "../../master/context/ShiftsContext";
import { getRequest } from "services/api";
import "react-datepicker/dist/react-datepicker.css";
import "../attendance-recap-page.css";
import AttendanceHubToolbar from "./attendance-hub-toolbar";

registerLocale("id", id);

const RECAP_PER_PAGE = 100;

const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value || 0);

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "-";
  return `${Math.floor(seconds / 3600)}j ${Math.floor((seconds % 3600) / 60)}m`;
};

const getDateFromValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return new Date(value);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
};

const getDateKey = (value) => {
  const date = getDateFromValue(value);
  if (!date || Number.isNaN(date.getTime())) return "";
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
};

const getDaysInRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const result = [];
  const cursor = new Date(startDate);
  const end = new Date(endDate);
  cursor.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  while (cursor <= end) {
    result.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

const toNumber = (value) => Number(value || 0);

const formatDateForFile = (value) => getDateKey(value) || "all";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatAttendanceTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
};

const normalizeRecapTotals = (totals = {}) => {
  totals = totals || {};
  return {
    present: toNumber(totals.present ?? totals.total_present),
    active: toNumber(totals.active),
    late: toNumber(totals.late),
    overtime: toNumber(totals.overtime),
    early: toNumber(totals.early),
    dayOff: toNumber(totals.day_off ?? totals.dayOff),
    scheduled: toNumber(totals.scheduled),
    publicHoliday: toNumber(totals.public_holiday ?? totals.publicHoliday),
    totalDuration: toNumber(totals.total_duration ?? totals.totalDuration),
    workDays: toNumber(totals.work_days ?? totals.workDays),
    morningMealTotal: toNumber(totals.morning_meal_total ?? totals.morningMealTotal ?? totals.total_morning_meal_cost),
  };
};

const getCellTone = (type) => {
  const normalized = String(type || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const tones = {
    hadir: "present",
    present: "present",
    aktif: "active",
    active: "active",
    terlambat: "late",
    late: "late",
    lembur: "overtime",
    overtime: "overtime",
    awal: "early",
    early: "early",
    libur: "dayoff",
    day_off: "dayoff",
    dayoff: "dayoff",
    libur_nasional: "publicholiday",
    public_holiday: "publicholiday",
    publicholiday: "publicholiday",
    kosong: "empty",
    empty: "empty",
  };
  return tones[normalized] || normalized || "empty";
};

const getScheduleLabel = (dailyItem = {}, shiftMap = new Map()) => {
  const cell = dailyItem.cell || {};
  const schedule = dailyItem.schedule || {};
  const tone = getCellTone(cell.type || dailyItem.type || (cell.is_empty ? "empty" : ""));

  if (schedule.is_day_off || schedule.is_public_holiday || tone === "dayoff" || tone === "publicholiday") {
    return "DO";
  }

  const shift = schedule.shift || schedule.ahr_shift || (schedule.shift_id ? shiftMap.get(String(schedule.shift_id)) : null);
  return (
    shift?.code ||
    schedule.shift_code ||
    schedule.shift?.code ||
    (shift?.name ? shift.name.charAt(0) : "") ||
    (schedule.shift_name ? schedule.shift_name.charAt(0) : "")
  );
};

const normalizeDailyCell = (dailyItem = {}, shiftMap = new Map()) => {
  const cell = dailyItem.cell || {};
  const tone = getCellTone(cell.type || dailyItem.type || (cell.is_empty ? "empty" : ""));
  const attendance = dailyItem.attendance || {};
  const hasAttendanceTime = Boolean(attendance.check_in);
  const label = hasAttendanceTime
    ? formatAttendanceTime(attendance.check_in)
    : getScheduleLabel(dailyItem, shiftMap) || (tone === "empty" ? "-" : "");
  const subLabel = hasAttendanceTime
    ? (attendance.check_out ? formatAttendanceTime(attendance.check_out) : "--:--")
    : cell.sub_label ?? cell.subLabel ?? dailyItem.sub_label ?? dailyItem.subLabel ?? "";
  return {
    label,
    subLabel,
    tone,
    color: cell.color,
    textColor: cell.text_color ?? cell.textColor,
    isEmpty: Boolean(cell.is_empty ?? dailyItem.is_empty),
  };
};

const normalizeRecapEmployee = (employee = {}) => {
  const user = employee.user || {};
  const rawDepartment = employee.department ?? user.department ?? {};
  const department = typeof rawDepartment === "string" ? { name: rawDepartment } : rawDepartment;
  return {
    ...employee,
    id: employee.id || employee.user_id || user.id,
    name: employee.name || user.name || "-",
    department: {
      id: department.id || employee.department_id || user.department_id || "",
      name: department.name || employee.department_name || "Unassigned",
    },
    day_off: employee.day_off || user.day_off || "",
    daily: Array.isArray(employee.daily) ? employee.daily : [],
    totals: normalizeRecapTotals(employee.totals),
  };
};

const normalizeMasterUserRecapRow = (user = {}, recapEmployee) => {
  const recap = recapEmployee || {};
  const rawDepartment = user.department ?? recap.department ?? {};
  const department = typeof rawDepartment === "string" ? { name: rawDepartment } : rawDepartment || {};
  const departmentId = user.department_id || department.id || recap.department_id || recap.department?.id || "";

  return {
    ...recap,
    id: user.id || recap.id,
    name: user.name || recap.name || "-",
    department: {
      id: departmentId,
      name: department.name || recap.department?.name || "Unassigned",
    },
    department_id: departmentId,
    day_off: user.day_off || recap.day_off || "",
    totals: recap.totals || normalizeRecapTotals({}),
    daily: Array.isArray(recap.daily) ? recap.daily : [],
  };
};

const getDefaultPeriod = () => {
  const now = new Date();
  const day = now.getDate();
  const month = now.getMonth();
  const year = now.getFullYear();
  return day >= 26
    ? { start: new Date(year, month, 26), end: new Date(year, month + 1, 25) }
    : { start: new Date(year, month - 1, 26), end: new Date(year, month, 25) };
};

const AttendanceHubRecapTab = () => {
  const { filterOptions, setFilterOptions } = useAttendance();
  const { setFilterOptions: setScheduleFilterOptions } = useSchedule();
  const { users } = useUser();
  const { shifts } = useShifts();
  const [showCalendarColumns, setShowCalendarColumns] = useState(true);
  const [cellWidth, setCellWidth] = useState(44);
  const [nameWidth, setNameWidth] = useState(135);
  const [exportLoading, setExportLoading] = useState(false);
  const [departmentDailySummary, setDepartmentDailySummary] = useState({ data: [], loading: false });
  const [attendanceRecapSummary, setAttendanceRecapSummary] = useState({ employees: [], summary: null, loading: false });

  const daysInRange = useMemo(() => getDaysInRange(filterOptions.startDate, filterOptions.endDate), [filterOptions.startDate, filterOptions.endDate]);

  useEffect(() => {
    const fetchAttendanceRecapSummary = async () => {
      if (!filterOptions.startDate || !filterOptions.endDate) return;

      setAttendanceRecapSummary((current) => ({ ...current, loading: true }));
      setDepartmentDailySummary((current) => ({ ...current, loading: true }));
      try {
        const params = new URLSearchParams({
          start_date: getDateKey(filterOptions.startDate),
          end_date: getDateKey(filterOptions.endDate),
          include_daily: "1",
          per_page: String(RECAP_PER_PAGE),
        });
        if (filterOptions.departmentId) params.append("department_id", filterOptions.departmentId);
        if (filterOptions.userId) params.append("user_id", filterOptions.userId);

        const employees = [];
        let summary = null;
        let departmentDaily = [];
        let page = 1;
        let lastPage = 1;

        do {
          params.set("page", String(page));
          const response = await getRequest(`/ahr/report/attendance-recap?${params.toString()}`);
          const payload = response.success ? (response.data || {}) : {};
          if (page === 1) summary = payload.summary || null;
          if (page === 1) departmentDaily = payload.department_daily_summary || [];
          employees.push(...(payload.employees || []).map(normalizeRecapEmployee));
          lastPage = Number(response.pagination?.last_page || payload.pagination?.last_page || 1);
          page += 1;
        } while (page <= lastPage);

        setAttendanceRecapSummary({
          employees,
          summary,
          loading: false,
        });
        setDepartmentDailySummary({
          data: departmentDaily,
          loading: false,
        });
      } catch (error) {
        console.error("Failed to fetch attendance recap summary", error);
        setAttendanceRecapSummary({ employees: [], summary: null, loading: false });
        setDepartmentDailySummary({ data: [], loading: false });
      }
    };

    fetchAttendanceRecapSummary();
  }, [filterOptions.startDate, filterOptions.endDate, filterOptions.departmentId, filterOptions.userId]);

  const updateFilters = (updater) => {
    setFilterOptions((previous) => updater(previous));
    setScheduleFilterOptions((previous) => updater(previous));
  };

  const handleGoToToday = () => {
    const period = getDefaultPeriod();
    updateFilters((previous) => ({ ...previous, startDate: period.start, endDate: period.end, currentPage: 1 }));
  };

  const handlePeriodChange = (direction) => {
    if (!filterOptions.startDate) return;
    const start = new Date(filterOptions.startDate);
    start.setMonth(start.getMonth() + direction);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(25);
    updateFilters((previous) => ({ ...previous, startDate: start, endDate: end, currentPage: 1 }));
  };

  const handleDateChange = (date, field) => updateFilters((previous) => ({ ...previous, [field]: date, currentPage: 1 }));

  const recapEmployees = useMemo(() => attendanceRecapSummary.employees || [], [attendanceRecapSummary.employees]);
  const recapEmployeeMap = useMemo(() => {
    const map = new Map();
    recapEmployees.forEach((employee) => {
      if (employee.id) map.set(String(employee.id), employee);
    });
    return map;
  }, [recapEmployees]);

  const shiftMap = useMemo(() => {
    const map = new Map();
    (shifts.data || []).forEach((shift) => {
      if (shift.id) map.set(String(shift.id), shift);
    });
    return map;
  }, [shifts.data]);

  const filteredUsers = useMemo(() => {
    let result = users.data || [];
    if (filterOptions.departmentId) {
      result = result.filter((user) => String(user.department?.id || user.department_id || "") === String(filterOptions.departmentId));
    }
    if (filterOptions.userId) {
      result = result.filter((user) => String(user.id) === String(filterOptions.userId));
    }
    return result;
  }, [users.data, filterOptions.departmentId, filterOptions.userId]);

  const rowsSource = useMemo(() => {
    const hasMasterUsers = (users.data || []).length > 0;
    if (!hasMasterUsers && !users.loading) return recapEmployees;
    return filteredUsers.map((user) => normalizeMasterUserRecapRow(user, recapEmployeeMap.get(String(user.id))));
  }, [filteredUsers, recapEmployeeMap, recapEmployees, users.data, users.loading]);

  const groupedUsers = useMemo(() => {
    const groups = {};
    rowsSource.forEach((user) => {
      const department = user.department?.name || "Unassigned";
      if (!groups[department]) groups[department] = [];
      groups[department].push(user);
    });
    return Object.entries(groups).sort(([first], [second]) => first.localeCompare(second));
  }, [rowsSource]);

  const userDailyMap = useMemo(() => {
    const map = new Map();
    rowsSource.forEach((employee) => {
      const dailyMap = new Map();
      employee.daily.forEach((item) => {
        const dateKey = getDateKey(item.date);
        if (dateKey) dailyMap.set(dateKey, normalizeDailyCell(item, shiftMap));
      });
      map.set(String(employee.id), dailyMap);
    });
    return map;
  }, [rowsSource, shiftMap]);

  const departmentDailyMap = useMemo(() => {
    const map = new Map();
    (departmentDailySummary.data || []).forEach((item) => {
      const departmentKey = item.department_id || item.department_name || "Unassigned";
      map.set(`${departmentKey}|${item.date}`, {
        working: Number(item.working_count || 0),
        total: Number(item.total_staff || 0),
        absent: Number(item.absent_count || 0),
        dayOff: Number(item.day_off_count || 0),
      });
    });

    return map;
  }, [departmentDailySummary.data]);

  const userSummary = useMemo(() => {
    const summaries = {};
    rowsSource.forEach((employee) => {
      if (!employee.id) return;
      summaries[employee.id] = employee.totals;
    });
    return summaries;
  }, [rowsSource]);

  const handleExportExcel = () => {
    if (!rowsSource.length) {
      alert("Tidak ada data rekap untuk diexport.");
      return;
    }

    setExportLoading(true);
    setTimeout(() => {
      try {
        const header = [
          "NO.",
          "Employee",
          ...daysInRange.map((date) => {
            const dayName = ["Ming", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"][date.getDay()];
            return `${dayName}, ${date.getDate()}/${date.getMonth() + 1}`;
          }),
          "PRS",
          "ACT",
          "LAT",
          "OT",
          "ERL",
          "DO",
          "SHIFT",
          "TOT JAM",
          "TTL KERJA",
          "UANG MAKAN",
        ];

        let rowNumber = 0;
        let tableContent = "";

        groupedUsers.forEach(([department, employees]) => {
          tableContent += `
            <tr style="background-color: #f1f5f9; color: #1e293b;">
              <td colspan="${header.length}" style="padding: 10px; font-weight: bold; border: 1px solid #cbd5e1; text-align: left;">
                ${escapeHtml(department.toUpperCase())}
              </td>
            </tr>
          `;

          employees.forEach((employee) => {
            rowNumber += 1;
            const summary = userSummary[employee.id] || {};
            let userRow = `
              <tr>
                <td style="padding: 8px; font-weight: bold; border: 1px solid #cbd5e1; text-align: center;">${rowNumber}</td>
                <td style="padding: 10px; font-weight: bold; border: 1px solid #cbd5e1; min-width: 180px;">${escapeHtml(employee.name || "-")}</td>
            `;

            daysInRange.forEach((date) => {
              const dateKey = getDateKey(date);
              const cell = userDailyMap.get(String(employee.id))?.get(dateKey) || { label: "-", subLabel: "", tone: "empty" };
              const isDayOff = cell.tone === "dayoff" || cell.tone === "publicholiday";
              const isEmpty = cell.tone === "empty";
              const bgColor = isDayOff || isEmpty ? "#fee2e2" : "#ffffff";
              const textColor = isDayOff || isEmpty ? "#b91c1c" : "#1f2937";

              userRow += `
                <td style="padding: 8px; background-color: ${bgColor}; color: ${textColor}; text-align: center; border: 1px solid #cbd5e1;">
                  <div style="font-weight: bold; font-size: 14px;">${escapeHtml(cell.label || "-")}</div>
                  <div style="font-size: 12px; opacity: 0.9;">${escapeHtml(cell.subLabel || "-")}</div>
                </td>
              `;
            });

            [
              summary.present || 0,
              summary.active || 0,
              summary.late || 0,
              summary.overtime || 0,
              summary.early || 0,
              summary.dayOff || 0,
              summary.scheduled || 0,
              formatDuration(summary.totalDuration),
              summary.workDays || 0,
              summary.morningMealTotal || 0,
            ].forEach((value) => {
              userRow += `<td style="padding: 8px; text-align: center; border: 1px solid #cbd5e1; font-weight: bold;">${escapeHtml(value)}</td>`;
            });

            userRow += "</tr>";
            tableContent += userRow;
          });
        });

        const fullHtml = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
            <head>
              <meta charset="UTF-8">
              <!--[if gte mso 9]>
              <xml>
                <x:ExcelWorkbook>
                  <x:ExcelWorksheets>
                    <x:ExcelWorksheet>
                      <x:Name>Rekap Matriks</x:Name>
                      <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                    </x:ExcelWorksheet>
                  </x:ExcelWorksheets>
                </x:ExcelWorkbook>
              </xml>
              <![endif]-->
              <style>
                table { border-collapse: collapse; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }
                th { background-color: #1e293b; color: #ffffff; padding: 12px; border: 1px solid #334155; text-align: center; font-size: 14px; text-transform: uppercase; }
                td { border: 1px solid #cbd5e1; font-size: 14px; }
              </style>
            </head>
            <body>
              <table>
                <thead><tr>${header.map((item) => `<th>${escapeHtml(item)}</th>`).join("")}</tr></thead>
                <tbody>${tableContent}</tbody>
              </table>
            </body>
          </html>
        `;

        const blob = new Blob([fullHtml], { type: "application/vnd.ms-excel" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Rekap_Matriks_Attendance_${formatDateForFile(filterOptions.startDate)}_to_${formatDateForFile(filterOptions.endDate)}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to export attendance recap", error);
        alert(error.message || "Gagal export rekap matriks.");
      } finally {
        setExportLoading(false);
      }
    }, 100);
  };

  if ((attendanceRecapSummary.loading || users.loading) && rowsSource.length === 0) {
    return <div className="d-flex justify-content-center align-items-center rekap-loading"><div className="spinner-border text-primary" role="status" /></div>;
  }

  const calendarStyle = { "--recap-name-width": `${nameWidth}px`, "--recap-cell-width": `${cellWidth}px` };

  return (
    <div className="attendance-recap-page p-3" style={calendarStyle}>
      <AttendanceHubToolbar
        startDate={filterOptions.startDate}
        endDate={filterOptions.endDate}
        departmentId={filterOptions.departmentId}
        userId={filterOptions.userId}
        onDateChange={handleDateChange}
        onPreviousPeriod={() => handlePeriodChange(-1)}
        onNextPeriod={() => handlePeriodChange(1)}
        onToday={handleGoToToday}
        onFilterChange={(changes) => setFilterOptions((previous) => ({ ...previous, ...changes, currentPage: 1 }))}
        adjustControls={(
          <>
            <label className="form-label small fw-bold text-muted mb-1">Employee Column ({nameWidth}px)</label>
            <input type="range" className="form-range mb-3" min="95" max="260" step="5" value={nameWidth} onChange={(event) => setNameWidth(Number(event.target.value))} />
            <label className="form-label small fw-bold text-muted mb-1">Daily Column ({cellWidth}px)</label>
            <input type="range" className="form-range mb-0" min="18" max="80" step="2" value={cellWidth} onChange={(event) => setCellWidth(Number(event.target.value))} />
          </>
        )}
        actionControls={(
          <>
            <button type="button" className="btn btn-sm btn-light border fw-bold text-success px-3 rounded" onClick={handleExportExcel} disabled={exportLoading || attendanceRecapSummary.loading || !rowsSource.length}>
              {exportLoading ? <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" /> : <IconFileSpreadsheet size={15} className="me-1" />}
              {exportLoading ? "Exporting..." : "Export Excel"}
            </button>
            <button type="button" className="btn btn-sm btn-light border fw-bold text-secondary px-3 rounded" onClick={() => setShowCalendarColumns((current) => !current)}>
              {showCalendarColumns ? <IconEyeOff size={15} className="me-1" /> : <IconEye size={15} className="me-1" />}
              {showCalendarColumns ? "Hide Calendar" : "Show Calendar"}
            </button>
          </>
        )}
      />

      <div className="card border-0 shadow-sm overflow-hidden mb-4">
        <AttendanceRecapContent daysInRange={daysInRange} groupedUsers={groupedUsers} userDailyMap={userDailyMap} userSummary={userSummary} showCalendarColumns={showCalendarColumns} departmentDailyMap={departmentDailyMap} departmentSummaryLoading={departmentDailySummary.loading} cellWidth={cellWidth} nameWidth={nameWidth} />
      </div>
    </div>
  );
};

const AttendanceRecapContent = ({ daysInRange, groupedUsers, userDailyMap, userSummary, showCalendarColumns, departmentDailyMap, departmentSummaryLoading, cellWidth, nameWidth }) => {
  let rowNumber = 0;
  const summaryColumnWidth = 320;
  const recapTableMinWidth = 35 + nameWidth + (showCalendarColumns ? daysInRange.length * cellWidth : 0) + summaryColumnWidth;
  return (
  <div className="card-body p-0">
    <div className="rekap-content-heading px-3 pt-3">
      <div>
        <h3 className="card-title mb-1">Matriks Absensi Harian</h3>
        <div className="text-muted small">Kolom tanggal dapat disembunyikan tanpa menghilangkan summary per karyawan.</div>
      </div>
      <div className="d-flex flex-wrap gap-2 rekap-legend">
        <span className="badge rekap-neutral-badge">Data Absensi</span>
        <span className="badge bg-red-lt text-red">Day Off</span>
        <span className="badge bg-red-lt text-red">Kosong</span>
      </div>
    </div>
    <div className="rekap-table-wrap">
      <table className="table table-vcenter card-table rekap-table mb-0" style={{ minWidth: `${recapTableMinWidth}px` }}>
        <thead>
          <tr>
            <th className="recap-sticky recap-no-col">NO.</th>
            <th className="recap-sticky recap-name-col">Employees</th>
            {showCalendarColumns && daysInRange.map((date) => {
              const active = getDateKey(date) === getDateKey(new Date());
              return (
                <th key={`${getDateKey(date)}-date`} className={`recap-day-col recap-date-head ${active ? "active" : ""}`}>
                  <div className="recap-date-weekday">{date.toLocaleDateString("id-ID", { weekday: "short" })}</div>
                  <div className="recap-date-number">{date.getDate()}</div>
                  {active && <div className="recap-date-dot" />}
                </th>
              );
            })}
            <th>PRS</th><th>ACT</th><th>LAT</th><th>OT</th><th>ERL</th><th>DO</th><th>SHIFT</th><th>TOT JAM</th><th>TTL KERJA</th><th>UANG MAKAN</th>
          </tr>
        </thead>
        <tbody>
          {groupedUsers.length > 0 ? groupedUsers.map(([department, employees]) => <React.Fragment key={department}>
            <tr className="recap-group-row">
              <td className="recap-sticky recap-group-label" colSpan="2">{department}</td>
              {showCalendarColumns && daysInRange.map((date) => {
                const dateKey = getDateKey(date);
                const departmentId = employees[0]?.department?.id || employees[0]?.department_id || department;
                const summary = departmentDailyMap.get(`${departmentId}|${dateKey}`) || departmentDailyMap.get(`${department}|${dateKey}`);
                return (
                  <td className="recap-day-col rekap-department-summary-cell" key={`${department}-${dateKey}-summary`} title={summary ? `${department}: ${summary.working} kerja dari ${summary.total} staff (${summary.absent} absen, ${summary.dayOff} day off)` : `${department}: menunggu data API`}>
                    {departmentSummaryLoading ? "..." : (summary ? `${summary.working}/${summary.total}` : "-")}
                  </td>
                );
              })}
              <td colSpan="10" />
            </tr>
            {employees.map((employee) => {
              const summary = userSummary[employee.id];
              rowNumber += 1;
              return <tr key={employee.id}>
                <td className="recap-sticky recap-no-col text-muted fw-bold">{rowNumber}</td>
                <td className="recap-sticky recap-name-col">
                  <div className="d-flex align-items-center">
                    <span className="fw-bold text-dark text-truncate">{employee.name}</span>
                  </div>
                </td>
                {showCalendarColumns && daysInRange.map((date) => {
                  const dateKey = getDateKey(date);
                  const cell = userDailyMap.get(String(employee.id))?.get(dateKey);
                  return <td className="recap-day-col" key={`${employee.id}-${dateKey}`}>{cell ? <AttendanceCell cell={cell} /> : <AttendanceCell cell={{ label: "-", subLabel: "", tone: "empty" }} />}</td>;
                })}
                <td className="text-success fw-bold">{summary?.present || 0}</td>
                <td className="text-warning fw-bold">{summary?.active || 0}</td>
                <td className="text-danger fw-bold">{summary?.late || 0}</td>
                <td className="text-primary fw-bold">{summary?.overtime || 0}</td>
                <td className="text-warning fw-bold">{summary?.early || 0}</td>
                <td className="text-danger fw-bold">{summary?.dayOff || 0}</td>
                <td className="text-blue fw-bold">{summary?.scheduled || 0}</td>
                <td>{formatDuration(summary?.totalDuration)}</td>
                <td className="fw-bold">{summary?.workDays || 0}</td>
                <td className="fw-bold">{formatCurrency(summary?.morningMealTotal)}</td>
              </tr>;
            })}
          </React.Fragment>) : <EmptyRow colSpan={(showCalendarColumns ? daysInRange.length : 0) + 12} message="Tidak ada data user untuk filter yang dipilih." />}
        </tbody>
      </table>
    </div>
  </div>
  );
};

const AttendanceCell = ({ cell }) => {
  return (
    <div className={`recap-cell recap-cell-${cell.tone}`} title={cell.subLabel}>
      <div className="recap-cell-main">{cell.label || "-"}</div>
      {cell.subLabel && <div className="recap-cell-sub">{cell.subLabel}</div>}
      {cell.tone !== "empty" && <span className="recap-cell-dot" />}
    </div>
  );
};

const EmptyRow = ({ colSpan, message }) => <tr><td colSpan={colSpan} className="text-center py-5"><div className="empty"><div className="empty-icon"><IconInfoCircle size={36} className="text-muted opacity-25" /></div><p className="empty-title mb-1">Belum ada data</p><p className="empty-subtitle text-muted mb-0">{message}</p></div></td></tr>;

export default AttendanceHubRecapTab;
